import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEdit, FaCheck } from 'react-icons/fa';
import Notification from '../shared/Notification';
import styles from './reset-password.module.css';
import NavHeader from './NavHeader';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const storedEmailOrPhone = sessionStorage.getItem('verifyEmailOrPhone') || '';
    const [originalEmailOrPhone, setOriginalEmailOrPhone] = useState(storedEmailOrPhone);
    const [formData, setFormData] = useState({ emailOrPhone: storedEmailOrPhone, verificationCode: '' });
    const [newFormData, setNewFormData] = useState({ emailOrPhone: storedEmailOrPhone, newPassword: '' });

    const [isVerifyDisabled, setIsVerifyDisabled] = useState(false);
    const [notification, setNotification] = useState('');
    const [setPasswordSegment, setSetPasswordSegment] = useState(true);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (e.target.name === 'emailOrPhone' && e.target.value !== originalEmailOrPhone) {
            setIsVerifyDisabled(true);
        }
    };

    const handleChangeNewPassword = (e) => {
        setNewFormData({ ...newFormData, [e.target.name]: e.target.value });
    };

    const handleEditClick = () => {
        setIsEditing(true);
        setIsVerifyDisabled(true);
    };

    const handleBlur = () => {
        setIsEditing(false);
        if (formData.emailOrPhone === originalEmailOrPhone) {
            setIsVerifyDisabled(false);
        }
        handleResend();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/authApplicant/verify-forget-password-code', formData);
            const { token } = response.data;
            localStorage.setItem('token', token);
            sessionStorage.removeItem('verifyEmailOrPhone');
            if (response.data.success) {
                setSetPasswordSegment(false);
                setNotification('Verification successful! Please set your new password.');
            }
        } catch (error) {
            setNotification(error.response.data.message);
        }
    };

    const handleSubmitNewPassword = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/authApplicant/reset-password', {
                token: localStorage.getItem('token'),
                newPassword: newFormData.newPassword
            });
            const { token } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('notification', 'Password reset successful!');
            sessionStorage.removeItem('verifyEmailOrPhone');
            navigate('/');
        } catch (error) {
            setNotification(error.response.data.message);
        }
    };

    const handleResend = async () => {
        setNotification('Resending verification code...');
        setIsVerifyDisabled(false);
        try {
            const payload = {
                emailOrPhone: originalEmailOrPhone,
                newEmailOrPhone: formData.emailOrPhone
            };
            setOriginalEmailOrPhone(formData.emailOrPhone);
            sessionStorage.setItem('verifyEmailOrPhone', formData.emailOrPhone);
            const response = await axios.post('/api/authApplicant/resend-forget-password', payload);
            if (response.data.success) {
                setNotification('Verification code sent.');
            }
        } catch (error) {
            // silent
        }
    };

    return (
        <>
        <NavHeader />
        {notification && (
            <Notification message={notification} onClose={() => setNotification('')} />
        )}

        <div className={styles.resetPage}>
            <div className={styles.card}>

                {setPasswordSegment ? (
                    <>
                    <div className={styles.cardHeader}>
                        <h1 className={styles.headline}>Reset password 🔑</h1>
                        <p className={styles.subtext}>Enter the code we sent to verify it's you.</p>
                    </div>

                    <div className={styles.cardBody}>
                        {/* Destination row */}
                        <div className={styles.destinationRow}>
                            <span className={styles.destinationLabel}>Sent to:</span>
                            {isEditing ? (
                                <input
                                    className={styles.editingInput}
                                    type="text"
                                    name="emailOrPhone"
                                    value={formData.emailOrPhone}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    autoFocus
                                />
                            ) : (
                                <>
                                    <span className={styles.destinationValue}>{formData.emailOrPhone}</span>
                                    <button type="button" className={styles.editBtn} onClick={handleEditClick} title="Edit">
                                        <FaEdit size={14} />
                                    </button>
                                </>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} noValidate className={styles.resetPasswordForm}>
                            <div className={styles.fieldWrap}>
                                <span className={styles.fieldIcon}>🔢</span>
                                <input
                                    className={styles.input}
                                    type="text"
                                    name="verificationCode"
                                    placeholder="6-digit code"
                                    value={formData.verificationCode}
                                    onChange={handleChange}
                                    maxLength={6}
                                    required
                                />
                            </div>

                            {error && <div className={styles.errorMsg}>{error}</div>}

                            <button type="submit" className={styles.submitBtn} disabled={isVerifyDisabled}>
                                Verify →
                            </button>
                            <button type="button" className={styles.resendBtn} onClick={handleResend}>
                                Resend code
                            </button>
                        </form>
                    </div>
                    </>
                ) : (
                    <>
                    <div className={styles.cardHeader}>
                        <h1 className={styles.headline}>Set new password 🔐</h1>
                        <p className={styles.subtext}>Choose a strong password for your account.</p>
                    </div>

                    <div className={styles.cardBody}>
                        {/* Verified badge row */}
                        <div className={styles.destinationRow}>
                            <span className={styles.destinationLabel}>Verified:</span>
                            <span className={styles.destinationValue}>{newFormData.emailOrPhone}</span>
                            <span className={styles.verifiedBadge}>
                                <FaCheck size={10} /> Done
                            </span>
                        </div>

                        <form onSubmit={handleSubmitNewPassword} noValidate className={styles.resetPasswordForm}>
                            <div className={styles.fieldWrap}>
                                <span className={styles.fieldIcon}>🔑</span>
                                <input
                                    className={`${styles.input} ${styles.inputPassword}`}
                                    type={showNewPassword ? "text" : "password"}
                                    name="newPassword"
                                    placeholder="New password"
                                    value={newFormData.newPassword}
                                    onChange={handleChangeNewPassword}
                                    required
                                />
                                {/* <button
                                    type="button"
                                    className={styles.eyeBtn}
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                                </button> */}
                            </div>

                            {error && <div className={styles.errorMsg}>{error}</div>}

                            <button type="submit" className={styles.submitBtn}>
                                Reset password →
                            </button>
                        </form>
                    </div>
                    </>
                )}

            </div>
        </div>
        </>
    );
};

export default ResetPassword;